/**
 * Transaction Service - N8N Webhook Integration (FIXED)
 * Handles customer inquiries through N8N webhooks following the Product Management module pattern
 */

interface TransactionRequest {
  path: string;
  method: string;
  data?: any;
  token?: string;
}

interface TransactionResponse {
  success: boolean;
  data?: any;
  error?: string;
}

class TransactionService {
  private readonly N8N_WEBHOOK_BASE = 'https://n8n.tenear.com/webhook';
  private readonly DEFAULT_TIMEOUT = 30000; // 30 seconds

  /**
   * Generic N8N webhook request handler with enhanced error handling
   */
  async callWebhook(path: string, data: any, token: string): Promise<TransactionResponse> {
    console.log('🚀 N8N Webhook Call:', { path, data, token: token ? 'present' : 'missing' });

    try {
      const url = `${this.N8N_WEBHOOK_BASE}/${path}`;
      console.log('📍 Webhook URL:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ path, ...data })
      });

      console.log('📡 Response Status:', response.status, response.statusText);
      console.log('📡 Response Headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ N8N HTTP Error:', errorText);
        throw new Error(`N8N Webhook HTTP ${response.status}: ${errorText}`);
      }

      // Handle empty responses (content-length: 0)
      const contentLength = response.headers.get('content-length');
      if (contentLength === '0' || !response.body) {
        console.log('⚠️ N8N returned empty response (content-length: 0)');
        return {
          success: true,
          data: [] // Return empty array for empty responses
        };
      }

      const result = await response.json();
      console.log('📊 N8N Response:', result);
      
      return {
        success: true,
        data: result
      };

    } catch (error) {
      console.error('❌ N8N Webhook Error Details:', error);
      
      // Don't hide the real error with generic message
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown N8N error'
      };
    }
  }

  /**
   * Get customer inquiries/transactions for a shop
   */
  async getTransactions(shopId: number, token: string, filters?: {
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
  }): Promise<TransactionResponse> {
    console.log('🎯 GET TRANSACTIONS:', { shopId, token: token ? 'present' : 'missing', filters });

    const requestData: Record<string, any> = {
      shop_id: shopId,
      date_filter: filters?.dateFrom ? 'custom' : 'week',
      status_filter: filters?.status || 'all',
      limit: filters?.limit || 100
    };

    if (filters?.dateFrom) {
      requestData.date_from = filters.dateFrom;
    }
    if (filters?.dateTo) {
      requestData.date_to = filters.dateTo;
    }

    return this.callWebhook('get-transactions', requestData, token);
  }

  /**
   * Update transaction/inquiry status
   */
  async updateTransaction(transactionId: string, updates: {
    status?: 'new' | 'contacted' | 'resolved';
    notes?: string;
    assigned_to?: string;
  }, token: string): Promise<TransactionResponse> {
    console.log('🔄 UPDATE TRANSACTION:', { transactionId, updates, token: token ? 'present' : 'missing' });

    const requestData = {
      path: 'update-transaction', // This tells the Switch node this is an UPDATE request
      transaction_id: transactionId,
      ...updates
    };

    return this.callWebhook('get-transactions', requestData, token);
  }

  /**
   * Get transaction statistics for a shop
   */
  async getTransactionStats(shopId: number, token: string): Promise<TransactionResponse> {
    console.log('📊 GET TRANSACTION STATS:', { shopId, token: token ? 'present' : 'missing' });

    const requestData = {
      shop_id: shopId,
      stats_only: true
    };

    return this.callWebhook('get-transactions', requestData, token);
  }

  /**
   * Test N8N webhook connectivity
   */
  async testWebhook(path: string): Promise<TransactionResponse> {
    console.log('🧪 TESTING N8N WEBHOOK:', path);
    
    const testData = { test: true, timestamp: new Date().toISOString() };
    return this.callWebhook(path, testData, '');
  }

  /**
   * Send a response to a customer inquiry via N8N webhook
   * Response is stored in database - customer sees it in their app (like broadcast)
   */
  async sendInquiryResponse(inquiryId: string, responseMessage: string, shopOwnerId: number, shopOwnerName: string, shopId: number, shopName: string, token: string): Promise<TransactionResponse> {
    console.log('📤 SENDING INQUIRY RESPONSE:', { inquiryId, responseLength: responseMessage.length, token: token ? 'present' : 'missing' });

    const requestData = {
      path: 'save-inquiry-response', // This tells the Switch node this is a SAVE RESPONSE request
      inquiry_id: inquiryId,
      shop_id: shopId,
      shop_owner_id: shopOwnerId,
      shop_owner_name: shopOwnerName,
      shop_name: shopName,
      response_message: responseMessage
    };

    return this.callWebhook('save-inquiry-response', requestData, token);
  }

  /**
   * Get responses for a specific inquiry (for dashboard history)
   */
  async getInquiryResponses(inquiryId: string, token: string): Promise<TransactionResponse> {
    console.log('📥 GETTING INQUIRY RESPONSES:', { inquiryId, token: token ? 'present' : 'missing' });

    const requestData = {
      inquiry_id: inquiryId
    };

    return this.callWebhook('get-inquiry-responses', requestData, token);
  }
}

// Export singleton instance
export const transactionService = new TransactionService();
export default transactionService;
